/**
 * Big Toe Energy — <bte-reviews> slider for the "Avis clients (slider)" block.
 *
 * - "slide" / "loop": native horizontal scroll with snap points (swipe works without any library)
 * - "fade": reviews stacked, cross-fade, swipe detected with pointer events
 * - Dots, arrows, autoplay (paused on hover, focus, touch, off screen and hidden tab)
 * - Adaptive height: the slider takes the height of the current review
 * - Theme editor: selecting a review block shows it
 */

const SWIPE_THRESHOLD = 40;

class BteReviews extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLButtonElement[]} */
  #dots = [];
  #index = 0;
  #timer = 0;
  #hovered = false;
  #focused = false;
  #visible = true;
  #scrollFrame = 0;
  #pointerX = null;
  /** @type {number | null} Slide we are scrolling to with a button, dot or autoplay */
  #scrollTarget = null;
  #scrollTargetTimer = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#track = this.querySelector('.bte-rv__track');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children).filter((el) => el.classList.contains('bte-rv__slide'));
    const count = this.#slides.length;

    this.#slides.forEach((slide, i) => slide.setAttribute('aria-label', `${i + 1} / ${count}`));
    if (count < 2) {
      this.#slides[0]?.classList.add('is-active');
      return;
    }

    const nav = this.querySelector('.bte-rv__nav');
    if (nav) nav.hidden = false;
    this.#buildDots();

    this.querySelector('[data-bte-prev]')?.addEventListener('click', this.#onPrev);
    this.querySelector('[data-bte-next]')?.addEventListener('click', this.#onNext);

    if (this.#isFade) {
      this.#track.addEventListener('pointerdown', this.#onPointerDown);
      this.#track.addEventListener('pointerup', this.#onPointerUp);
      this.#track.addEventListener('pointercancel', this.#onPointerCancel);
    } else {
      this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('touchstart', this.#onTouch, { passive: true });
    document.addEventListener('visibilitychange', this.#schedule);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        this.#schedule();
      });
      this.#observer.observe(this);
    }

    if (this.#adaptive && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateHeight());
      this.#slides.forEach((slide) => this.#resizeObserver?.observe(slide));
    }

    this.#setActive(0);
    this.classList.add('is-ready');
    this.#schedule();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#scrollTargetTimer);
    cancelAnimationFrame(this.#scrollFrame);
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#schedule);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
  }

  get #isFade() {
    return this.dataset.type === 'fade';
  }

  get #loops() {
    return this.dataset.type !== 'slide';
  }

  get #adaptive() {
    return this.dataset.adaptive === 'true';
  }

  get #autoplay() {
    return this.dataset.autoplay === 'true' && !this.#reduceMotion;
  }

  #buildDots() {
    const container = this.querySelector('[data-bte-dots]');
    if (!container) return;
    container.replaceChildren();
    this.#dots = this.#slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bte-rv__dot';
      dot.setAttribute('aria-label', `Review ${i + 1} of ${this.#slides.length}`);
      dot.addEventListener('click', () => this.goTo(i));
      container.append(dot);
      return dot;
    });
  }

  /** @param {number} index */
  goTo(index, { instant = false } = {}) {
    const count = this.#slides.length;
    if (count < 2 || !this.#track) return;
    const target = this.#loops ? (index + count) % count : Math.min(Math.max(index, 0), count - 1);

    if (!this.#isFade) {
      const smooth = !instant && !this.#reduceMotion;
      // Ignore the in-between slides while the smooth scroll passes over them
      this.#scrollTarget = smooth ? target : null;
      window.clearTimeout(this.#scrollTargetTimer);
      if (smooth) this.#scrollTargetTimer = window.setTimeout(() => (this.#scrollTarget = null), 1000);
      this.#track.scrollTo({ left: this.#slides[target].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    }
    this.#setActive(target);
    this.#schedule();
  }

  next() {
    const last = this.#slides.length - 1;
    // Autoplay always rewinds, even in "classic" mode
    if (this.#index >= last && !this.#loops) this.goTo(0);
    else this.goTo(this.#index + 1);
  }

  /** @param {number} index */
  #setActive(index) {
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      if (this.#isFade) slide.toggleAttribute('inert', !active);
    });
    this.#dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));

    if (!this.#loops) {
      const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bte-prev]'));
      const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bte-next]'));
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.#slides.length - 1;
    }
    this.#updateHeight();
  }

  #updateHeight() {
    if (!this.#adaptive || !this.#track) return;
    const slide = this.#slides[this.#index];
    if (slide) this.#track.style.height = `${slide.offsetHeight}px`;
  }

  #schedule = () => {
    window.clearTimeout(this.#timer);
    if (!this.#autoplay || this.#hovered || this.#focused || !this.#visible || document.hidden) return;
    const delay = (parseFloat(this.dataset.autoplaySpeed || '5') || 5) * 1000;
    this.#timer = window.setTimeout(() => this.next(), delay);
  };

  #onPrev = () => {
    this.goTo(this.#index - 1);
  };

  #onNext = () => {
    this.goTo(this.#index + 1);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      const track = this.#track;
      if (!track) return;
      const left = track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.#slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - left);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      if (this.#scrollTarget !== null) {
        if (closest === this.#scrollTarget && distance < 2) this.#scrollTarget = null;
        return;
      }
      if (closest !== this.#index) {
        this.#setActive(closest);
        this.#schedule();
      }
    });
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerX = event.clientX;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#pointerX === null) return;
    const dx = event.clientX - this.#pointerX;
    this.#pointerX = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0) this.goTo(this.#index + 1);
    else this.goTo(this.#index - 1);
  };

  #onPointerCancel = () => {
    this.#pointerX = null;
  };

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#schedule();
  };

  #onLeave = () => {
    this.#hovered = false;
    this.#schedule();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#schedule();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#schedule();
  };

  // Touching the slider restarts the autoplay countdown instead of jumping right away
  #onTouch = () => {
    this.#schedule();
  };

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index === -1) return;
    this.dataset.autoplay = 'false';
    this.goTo(index, { instant: true });
  };
}

if (!customElements.get('bte-reviews')) customElements.define('bte-reviews', BteReviews);
